import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

import { Button } from 'components/Form/Button/Button';
import { Input } from 'components/Form/Input/Input/Input';

import { isValidQuantity } from '../../helpers/quantityValidation';
import { LineItemIdentifiers } from '../LineItemIdentifiers/LineItemIdentifiers';

type PlacementQuantityDialogProps = {
  productName: string;
  identifiers: Array<string>;
  unitOfMeasure?: string;
  remaining: number;
  initialQuantity?: number;
  onConfirm: (quantity: number) => void;
  onCancel: () => void;
};

/**
 * The "available" line appends the unit of measure only when present; the variant
 * is selected with a ternary inside t() so the i18next-parser keeps both literals.
 *
 * i18n keys
 * t('webedi:packing_list.placement_dialog.available', { available })
 * t('webedi:packing_list.placement_dialog.available_with_unit', { available, unit })
 */
export const PlacementQuantityDialog = ({
  productName,
  identifiers,
  unitOfMeasure,
  remaining,
  initialQuantity,
  onConfirm,
  onCancel,
}: PlacementQuantityDialogProps) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(initialQuantity ?? remaining);
  // Whether to show an error at all — validity (below) decides whether there is one to show, so
  // this only needs to flip on once, at the first submit attempt.
  const [showError, setShowError] = useState(false);
  const hasError = showError && !isValidQuantity(quantity, remaining);

  const availableLabel = unitOfMeasure
    ? t('webedi:packing_list.placement_dialog.available_with_unit', { available: remaining, unit: unitOfMeasure })
    : t('webedi:packing_list.placement_dialog.available', { available: remaining });

  const updateQuantity = (next: number) => setQuantity(next);

  const handleAdd = () => {
    if (!isValidQuantity(quantity, remaining)) {
      setShowError(true);
      return;
    }
    onConfirm(quantity);
  };

  return (
    <Dialog open onClose={onCancel} className="relative z-60">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
      <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
          <p className="text-sm font-semibold text-gray-900">{productName}</p>
          <LineItemIdentifiers
            parts={[...identifiers, availableLabel]}
            className="mt-1 line-clamp-2 text-xs text-gray-500"
          />
          <div className="mt-4 flex items-center gap-2">
            <Button
              variant="secondary"
              size="base"
              iconOnly
              LeftIcon={MinusIcon}
              aria-label={t('webedi:packing_list.placement_dialog.decrease')}
              onClick={() => updateQuantity(Math.max(1, quantity - 1))}
              analyticsId="webedi:packing_list_placement_decrease"
            >
              {t('webedi:packing_list.placement_dialog.decrease')}
            </Button>
            <Input
              type="number"
              aria-label={t('webedi:packing_list.placement_dialog.quantity_label')}
              value={quantity}
              fieldState={hasError ? 'error' : 'default'}
              className="text-center"
              wrapperClassName="min-w-0 flex-1"
              onChange={(event) => updateQuantity(Number(event.target.value))}
              rightAddon={
                unitOfMeasure ? <span className="shrink-0 text-sm text-gray-500">{unitOfMeasure}</span> : null
              }
            />
            <Button
              variant="secondary"
              size="base"
              iconOnly
              LeftIcon={PlusIcon}
              aria-label={t('webedi:packing_list.placement_dialog.increase')}
              onClick={() => updateQuantity(Math.min(remaining, quantity + 1))}
              analyticsId="webedi:packing_list_placement_increase"
            >
              {t('webedi:packing_list.placement_dialog.increase')}
            </Button>
          </div>
          {hasError && (
            <p className="mt-2 text-sm text-red-600">
              {t('webedi:packing_list.placement_dialog.error.invalid_quantity', { available: remaining })}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} analyticsId="webedi:packing_list_placement_cancel">
              {t('common:cancel')}
            </Button>
            <Button onClick={handleAdd} analyticsId="webedi:packing_list_placement_add">
              {t('webedi:packing_list.placement_dialog.add')}
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
